import * as THREE from 'three';
import glintFragment from './shaders/boxGlint.frag' with { type: 'text' };
import glintUniforms from './shaders/boxGlintUniforms.glsl' with { type: 'text' };

export type GlintOptions = {
  period?: number;
  speed?: number;
};

type PatchState = {
  material: THREE.Material;
  onBeforeCompile: (
    shader: THREE.WebGLProgramParametersWithUniforms,
    renderer: THREE.WebGLRenderer,
  ) => void;
  customProgramCacheKey: () => string;
};

export class GlintEffect {
  private time = 0;
  private readonly uniforms = {
    uGlintTime: { value: 0 },
    uGlintStrength: { value: 0 },
    uGlintPeriod: { value: 2 },
    uGlintSpeed: { value: 8 },
  };
  private readonly patches: PatchState[] = [];

  constructor(
    materials: THREE.Material | THREE.Material[],
    options: GlintOptions = {},
  ) {
    this.uniforms.uGlintPeriod.value = options.period ?? 2;
    this.uniforms.uGlintSpeed.value = options.speed ?? 8;

    const materialList = Array.isArray(materials) ? materials : [materials];
    for (const material of materialList) {
      this.patchMaterial(material);
    }
  }

  update(deltaSeconds: number) {
    if (this.uniforms.uGlintStrength.value <= 0) {
      return;
    }

    this.time += deltaSeconds;
    this.uniforms.uGlintTime.value = this.time;
  }

  setSelected(selected: boolean) {
    this.uniforms.uGlintStrength.value = selected ? 1 : 0;
  }

  setPeriod(period: number) {
    this.uniforms.uGlintPeriod.value = period;
  }

  setSpeed(speed: number) {
    this.uniforms.uGlintSpeed.value = speed;
  }

  dispose() {
    for (const patch of this.patches) {
      patch.material.onBeforeCompile = patch.onBeforeCompile;
      patch.material.customProgramCacheKey = patch.customProgramCacheKey;
      patch.material.needsUpdate = true;
    }
    this.patches.length = 0;
  }

  private patchMaterial(material: THREE.Material) {
    const previousOnBeforeCompile = material.onBeforeCompile.bind(material);
    const previousCacheKey = material.customProgramCacheKey.bind(material);

    material.onBeforeCompile = (shader, renderer) => {
      previousOnBeforeCompile(shader, renderer);

      shader.uniforms.uGlintTime = this.uniforms.uGlintTime;
      shader.uniforms.uGlintStrength = this.uniforms.uGlintStrength;
      shader.uniforms.uGlintPeriod = this.uniforms.uGlintPeriod;
      shader.uniforms.uGlintSpeed = this.uniforms.uGlintSpeed;

      shader.fragmentShader = shader.fragmentShader.replace(
        'void main() {',
        `${glintUniforms}\nvoid main() {`,
      );
      shader.fragmentShader = shader.fragmentShader.replace(
        '#include <dithering_fragment>',
        glintFragment,
      );
    };

    material.customProgramCacheKey = () => `${previousCacheKey()}|glint-v1`;
    material.needsUpdate = true;

    this.patches.push({
      material,
      onBeforeCompile: previousOnBeforeCompile,
      customProgramCacheKey: previousCacheKey,
    });
  }
}
